import { FormEvent, ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import '../../styles/AdminStyles/WebinarEditor.css';
import {
  isAdminError, isArchivedStatus, reopensWebinar, Webinar, WebinarInput, WebinarStatus, WEBINAR_STATUSES, WEBINAR_STATUS_LABELS,
} from "../../webinars/types";
import { webinarRepository } from "../../webinars/repository";
import { PRESENTER_PHOTOS } from "../../webinars/photos";
import { fromSastInputs, slugify, toSastInputs } from "../../webinars/format";
import { validateWebinar, WebinarErrors } from "../../webinars/webinarValidation";

interface WebinarEditorProps {
  /** Leave out to add a new webinar */
  webinar?: Webinar;
  /** This round's active registrations: for the "attendees aren't told" note, and archived when reopening */
  registeredCount: number;
  onClose: () => void;
  notify: (message: string) => void;
}

const STATUS_HELP: Record<WebinarStatus, string> = {
  draft: "Hidden from the website. Registration is refused.",
  published: "Listed. Registration is open until the deadline, the start time or capacity is reached.",
  registration_closed: "Listed and marked “Registration closed”.",
  completed: "Shown under Past events, with the recording link if there is one.",
  cancelled: "Hidden from the list; its page says it's cancelled. Registered attendees aren't told automatically.",
};

const PLATFORMS = ["Microsoft Teams", "Zoom", "Google Meet", "YouTube Live"];

/** "tebogo-kole-outdoor" → "Tebogo Kole Outdoor" */
const photoLabel = (key: string) => key.split("-").map(word => word[0].toUpperCase() + word.slice(1)).join(" ");

type Form = Record<
  "title" | "slug" | "summary" | "description" | "topics" | "presenter" | "presenterRole" | "presenterPhoto" | "date" |
  "startTime" | "endTime" | "deadlineDate" | "deadlineTime" | "capacity" | "meetingPlatform" | "recordingUrl" | "resourceUrl",
  string
> & { status: WebinarStatus };

function toForm(w?: Webinar): Form {
  const start = toSastInputs(w?.startAt);
  const deadline = toSastInputs(w?.registrationDeadline);
  return {
    title: w?.title ?? "",
    slug: w?.slug ?? "",
    summary: w?.summary ?? "",
    description: w?.description ?? "",
    topics: (w?.topics ?? []).join("\n"),
    presenter: w?.presenter ?? "",
    presenterRole: w?.presenterRole ?? "",
    presenterPhoto: w?.presenterPhoto ?? "",
    date: start.date,
    startTime: w ? start.time : "18:00",
    endTime: w ? toSastInputs(w.endAt).time : "19:30",
    deadlineDate: deadline.date,
    deadlineTime: deadline.time,
    capacity: w?.capacity ? String(w.capacity) : "",
    meetingPlatform: w?.meetingPlatform ?? "Microsoft Teams",
    status: w?.status ?? "draft",
    recordingUrl: w?.recordingUrl ?? "",
    resourceUrl: w?.resourceUrl ?? "",
  };
}

/** Empty date or time gives "", which validation reports as missing */
const sastIso = (date: string, time: string) => {
  if (!date || !time) return "";
  try { return fromSastInputs(date, time); } catch { return ""; }
};

const optional = (value: string) => value.trim() || undefined;

function toInput(f: Form): WebinarInput {
  const hasDeadline = Boolean(f.deadlineDate || f.deadlineTime);
  return {
    title: f.title.trim(),
    slug: f.slug.trim(),
    summary: f.summary.trim(),
    description: f.description.trim(),
    topics: f.topics.split("\n").map(topic => topic.trim()).filter(Boolean),
    presenter: optional(f.presenter),
    presenterRole: optional(f.presenterRole),
    presenterPhoto: optional(f.presenterPhoto),
    startAt: sastIso(f.date, f.startTime),
    endAt: sastIso(f.date, f.endTime),
    registrationDeadline: hasDeadline ? sastIso(f.deadlineDate, f.deadlineTime) : undefined,
    capacity: f.capacity.trim() ? Number(f.capacity) : undefined,
    meetingPlatform: optional(f.meetingPlatform),
    status: f.status,
    recordingUrl: optional(f.recordingUrl),
    resourceUrl: optional(f.resourceUrl),
  };
}

/** Which form field shows each validation message, and gets focus when it's the first one */
const ERROR_FIELD: Partial<Record<keyof WebinarInput, string>> = {
  startAt: "we-date", endAt: "we-endTime", registrationDeadline: "we-deadlineDate",
};

// Adds or edits one webinar. Times are entered in South African time (SAST).
export default function WebinarEditor({ webinar, registeredCount, onClose, notify }: WebinarEditorProps) {
  const isNew = !webinar;
  const [form, setForm] = useState<Form>(() => toForm(webinar));
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [errors, setErrors] = useState<WebinarErrors>({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const initial = useRef(JSON.stringify(toForm(webinar)));
  const dirty = JSON.stringify(form) !== initial.current;
  // Saving an archived webinar as draft or published starts a new round
  const reopening = Boolean(webinar && reopensWebinar(webinar.status, form.status));
  const archiving = isArchivedStatus(form.status) && !(webinar && isArchivedStatus(webinar.status));
  const people = `${registeredCount} current registration${registeredCount === 1 ? "" : "s"}`;

  const close = () => {
    if (saving) return;
    if (dirty && !window.confirm("Discard your changes to this webinar?")) return;
    onClose();
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    document.getElementById("we-title")?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") closeRef.current(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const set = (field: keyof Form) => (e: { target: { value: string } }) => {
    const value = e.target.value;
    setForm(prev => ({
      ...prev,
      [field]: value,
      // A new webinar's web address follows its title until it's edited by hand
      ...(field === "title" && !slugTouched ? { slug: slugify(value) } : {}),
    }));
    if (field === "slug") setSlugTouched(true);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError("");
    const input = toInput(form);
    const found = validateWebinar(input);
    setErrors(found);
    const first = Object.keys(found)[0] as keyof WebinarInput | undefined;
    if (first) {
      setFormError("Check the highlighted fields.");
      document.getElementById(ERROR_FIELD[first] ?? `we-${first}`)?.focus();
      return;
    }

    if (reopening && registeredCount > 0 && !window.confirm(
      `Reopen this webinar for a new round?

The ${people} will be archived and the seat count restarts at 0. ` +
      "They stay in the attendee list under Archived and can register again. This can't be undone.",
    )) return;

    setSaving(true);
    try {
      await webinarRepository.saveWebinar(input, webinar?.id);
      notify(
        reopening ? (registeredCount > 0 ? `Webinar reopened: ${people} archived` : "Webinar reopened")
          : archiving ? "Webinar saved and moved to the archive"
          : isNew ? "Webinar added" : "Webinar saved",
      );
      onClose();
    } catch (error) {
      if (isAdminError(error) && error.code === "duplicate") {
        setErrors({ slug: error.message });
        document.getElementById("we-slug")?.focus();
      } else {
        setFormError(isAdminError(error) ? error.message : "Couldn't save the webinar. Check your connection and try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const field = (name: keyof Form, label: string, input: ReactNode, hint?: ReactNode, errorKey?: keyof WebinarInput) => {
    const error = errors[errorKey ?? (name as keyof WebinarInput)];
    return (
      <label className="wa-field">
        {label}
        {input}
        {hint && <small>{hint}</small>}
        {error && <small className="we-error">{error}</small>}
      </label>
    );
  };

  const text = (name: keyof Form, props: { type?: string; placeholder?: string; list?: string; required?: boolean } = {}, errorKey?: keyof WebinarInput) => (
    <input
      id={`we-${name}`}
      type={props.type ?? "text"}
      value={form[name]}
      onChange={set(name)}
      placeholder={props.placeholder}
      list={props.list}
      aria-invalid={Boolean(errors[errorKey ?? (name as keyof WebinarInput)])}
    />
  );

  return createPortal(
    <div className="wa-modal-backdrop">
      <form
        className="wa-modal wa-modal--wide we-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="we-heading"
        onSubmit={submit}
        noValidate
      >
        <div className="wa-modal-head">
          <div>
            <h2 id="we-heading">{isNew ? "Add a webinar" : "Edit webinar"}</h2>
            <p className="wa-muted we-intro">Times are South African time (SAST). Fields marked * are required.</p>
          </div>
          <button type="button" className="wa-modal-close" onClick={close} aria-label="Close">×</button>
        </div>

        {!isNew && registeredCount > 0 && !reopening && (
          <p className="wa-warning we-note">
            {registeredCount} {registeredCount === 1 ? "person is" : "people are"} registered. They aren't emailed about
            changes automatically: if the date, time or joining link changes, use Joining instructions → Resend to everyone.
          </p>
        )}

        <fieldset className="we-group">
          <legend>About the webinar</legend>
          {field("title", "Title *", text("title"))}
          {field("slug", "Web address *", text("slug"),
            <>kolemedical.co.za/events/<strong>{form.slug || "…"}</strong>
              {!isNew && webinar?.status !== "draft" && form.slug !== webinar?.slug && <> · Changing it breaks links people already have.</>}
            </>)}
          {field("summary", "Summary *", <textarea id="we-summary" rows={2} value={form.summary} onChange={set("summary")} aria-invalid={Boolean(errors.summary)} />,
            "One or two sentences for the webinar card.")}
          {field("description", "Description *", <textarea id="we-description" rows={5} value={form.description} onChange={set("description")} aria-invalid={Boolean(errors.description)} />,
            "The full description on the webinar's page.")}
          {field("topics", "Topics", <textarea id="we-topics" rows={3} value={form.topics} onChange={set("topics")} aria-invalid={Boolean(errors.topics)} />,
            "One per line. Listed under “Topics covered” on the webinar's page.")}
        </fieldset>

        <fieldset className="we-group">
          <legend>Date and time</legend>
          <div className="we-row">
            {field("date", "Date *", text("date", { type: "date" }, "startAt"), undefined, "startAt")}
            {field("startTime", "Starts *", text("startTime", { type: "time" }, "startAt"))}
            {field("endTime", "Ends *", text("endTime", { type: "time" }, "endAt"), undefined, "endAt")}
          </div>
          <div className="we-row">
            {field("deadlineDate", "Registration closes (date)", text("deadlineDate", { type: "date" }, "registrationDeadline"), undefined, "registrationDeadline")}
            {field("deadlineTime", "Registration closes (time)", text("deadlineTime", { type: "time" }, "registrationDeadline"),
              "Leave both empty to close registration when the webinar starts.")}
          </div>
        </fieldset>

        <fieldset className="we-group">
          <legend>Registration and format</legend>
          <div className="we-row">
            {field("status", "Status *", (
              <select id="we-status" value={form.status} onChange={set("status")}>
                {WEBINAR_STATUSES.map(status => <option key={status} value={status}>{WEBINAR_STATUS_LABELS[status]}</option>)}
              </select>
            ), STATUS_HELP[form.status])}
            {field("capacity", "Seats", text("capacity", { type: "number", placeholder: "No limit" }), "Leave empty for no limit.")}
          </div>
          {reopening && (
            <p className="wa-warning we-note" role="status">
              <strong>This starts a new round.</strong>{" "}
              {registeredCount > 0
                ? <>The {people} will be archived and the seat count restarts at 0. They stay in the attendee list under Archived, and can register again.</>
                : <>The seat count starts at 0.</>}
              {" "}Check the date and time above, and the joining link (kept from the last round) under Joining instructions.
            </p>
          )}
          {archiving && (
            <p className="wa-muted we-note">Saving moves this webinar to the Archive in the dashboard. On the website it shows as described above.</p>
          )}
          {field("meetingPlatform", "Platform", text("meetingPlatform", { list: "we-platforms" }),
            "Shown on the page and in emails, e.g. Microsoft Teams. The joining link itself is saved under Joining instructions, where only administrators can see it.")}
          <datalist id="we-platforms">{PLATFORMS.map(p => <option key={p} value={p} />)}</datalist>
        </fieldset>

        <fieldset className="we-group">
          <legend>Presenter</legend>
          <div className="we-row">
            {field("presenter", "Name", text("presenter"), "Leave empty if not confirmed: the page says “Presenter to be announced”.")}
            {field("presenterRole", "Role", text("presenterRole", { placeholder: "e.g. Founder, Kole Medical" }))}
          </div>
          {field("presenterPhoto", "Photo", (
            <select id="we-presenterPhoto" value={form.presenterPhoto} onChange={set("presenterPhoto")}>
              <option value="">No photo (show initials)</option>
              {Object.keys(PRESENTER_PHOTOS).map(key => <option key={key} value={key}>{photoLabel(key)}</option>)}
            </select>
          ), "Photos ship with the website. To add one, ask your developer (src/webinars/photos.ts).")}
        </fieldset>

        <fieldset className="we-group">
          <legend>After the event</legend>
          <div className="we-row">
            {field("recordingUrl", "Recording link", text("recordingUrl", { type: "url", placeholder: "https://" }))}
            {field("resourceUrl", "Resources link", text("resourceUrl", { type: "url", placeholder: "https://" }))}
          </div>
        </fieldset>

        {formError && <p className="wa-form-error" role="alert">{formError}</p>}

        <div className="we-actions">
          <button type="button" className="wa-btn wa-btn--quiet" onClick={close} disabled={saving}>Cancel</button>
          <button type="submit" className="km-btn km-btn--primary" disabled={saving}>
            {saving ? "Saving…" : isNew ? "Add webinar" : "Save changes"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
